import React, { useEffect, useState, useCallback } from "react";
import api from "../../api/client";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { Link, useLocation } from "react-router-dom";

// Tabs
import ProfileTab from "../../screens/profile/tabs/ProfileTab";
import AddressesTab from "../../screens/profile/tabs/AddressesTab";
import DevicesTab from "../../screens/profile/tabs/DevicesTab";
import MyServicesTab from "../../screens/profile/tabs/MyServicesTab";
import ConversationsTab from "../../screens/profile/tabs/ConversationsTab";
import MyFreelancerOrders from "../../pages/MyFreelancerOrders";
import MyInvoices from "./MyInvoices";
import ProfileWalletTab from "../../screens/profile/ProfileWalletTab";
import MyCourses from "../../pages/MyCourses";
import MyJobApplications from "../../pages/MyJobApplications";
import MyServiceOrders from "../../pages/MyServiceOrders";
import { getProviderBalanceBreakdown } from "../../api/freelancerServices";
import logger from "../../utils/logger.js";

// UI
import Breadcrumb from "../../components/ui/Breadcrumb/Breadcrumb";
import LoadingScreen from "../../components/ui/Loading/Loading";
import useBurgerMenu from "../../hooks/useBurgerMenu";
import {
  User,
  MapPin,
  Monitor,
  Wallet,
  Receipt,
  GraduationCap,
  Wrench,
  Briefcase,
  AlertTriangle,
  Store,
  ArrowUpRight,
  LayoutGrid,
  X,
  ShieldCheck,
  Sparkles,
  Phone,
  Mail,
  Coins,
  ChevronRight,
  ChevronLeft,
  MessageCircle,
  Clock,
} from "lucide-react";
import "./Account.css";

function getStorageBaseUrl() {
  let apiUrl = process.env.REACT_APP_API_URL || "";

  if (!apiUrl && typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  if (apiUrl.startsWith("/")) {
    const origin = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
    apiUrl = `${origin}${apiUrl}`;
  }

  return apiUrl
    .replace(/\/api(\/v1)?\/?$/, "")
    .replace(/\/laravel-api\/?$/, "")
    .replace(/\/$/, "");
}

function toAbsoluteUrl(raw) {
  const u = String(raw || "").trim();
  if (!u) return "";

  if (/^(blob:|data:)/i.test(u)) return u;

  const storageBase = getStorageBaseUrl();

  if (u.includes("/storage/")) {
    const storagePath = u.substring(u.indexOf("/storage/"));
    let clean = storagePath.replace(/^\/+/, "");
    if (clean.startsWith("storage/")) {
      clean = clean.replace(/^storage\//, "");
    }
    const fullPath = `${storageBase}/storage/${clean}`;
    if (fullPath.startsWith("/")) {
      const origin =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : "";
      return `${origin}${fullPath}`;
    }
    return fullPath;
  }

  if (/^https?:\/\//i.test(u)) return u;

  let clean = u.replace(/^\/+/, "");

  if (clean.startsWith("laravel-api/")) {
    clean = clean.replace(/^laravel-api\//, "");
  }

  if (clean.startsWith("storage/")) {
    clean = clean.replace(/^storage\//, "");
  }

  const fullPath = `${storageBase}/storage/${clean}`;

  if (fullPath.startsWith("/")) {
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";
    return `${origin}${fullPath}`;
  }

  return fullPath;
}

export default function Account() {
  const { lang } = useLanguage();
  const { user: authUser, updateUser: updateAuthUser } = useAuth();
  const isAr = lang === "ar";
  const location = useLocation();
  const hasBurgerMenu = useBurgerMenu();

  const [user, setUser] = useState(authUser);

  const [invoices, setInvoices] = useState([]);
  const [walletInfo, setWalletInfo] = useState(null);
  const [providerBalance, setProviderBalance] = useState(null);
  const [avatarHeroError, setAvatarHeroError] = useState(false);

  const [form, setForm] = useState({
    name: "",
    avatar_url: "",
    phone: "",
    address: "",
    street_address: "",
    neighborhood: "",
    city: "",
    country_name: "",
    country_code: "",
    password: "",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("profile");
  const [retryKey, setRetryKey] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const showProvider = true;

  const labels = {
    en: {
      myAccount: "My Account",
      accountSettings: "Account & Security",
      freelancerWorkspace: "Freelancer Hub",
      activitiesAndServices: "Client Activities",
      profile: "Profile Settings",
      addresses: "Addresses",
      devices: "Authorized Devices",
      wallet: "Wallet & Balance",
      invoices: "Invoices & Billing",
      courses: "My Courses",
      consultations: "Consultations",
      services: "Client Service Orders",
      myServices: "My Published Services",
      conversations: "Messages & Conversations",
      freelancerOrders: "My Freelancer Orders",
      jobs: "Job Applications",
      provider: "Provider Dashboard",
      menu: "All Navigation",
      allServices: "Account Workspace",
      loading: "Loading profile...",
      loadError: "Error loading account data",
      retry: "Try Again",
      memberBadge: "Verified Member",
      coinsBadge: "Coins Balance",
      totalBalanceBadge: "Total Balance",
      pendingBalanceBadge: "Pending Balance",
      invoicesCount: "Invoices",
    },
    ar: {
      myAccount: "حسابي الشخصي",
      accountSettings: "إعدادات الحساب والأمان",
      freelancerWorkspace: "ركن المستقل",
      activitiesAndServices: "أنشطتي كعميل",
      profile: "الملف الشخصي",
      addresses: "العناوين المحفوظة",
      devices: "الأجهزة المصرحة",
      wallet: "المحفظة والرصيد",
      invoices: "الفواتير والمعاملات",
      courses: "دوراتي وكورساتي",
      consultations: "استشاراتي",
      services: "طلبات الشراء (كعميل)",
      myServices: "خدماتي المنشورة",
      conversations: "المحادثات والرسائل",
      freelancerOrders: "طلباتي كمستقل",
      jobs: "طلبات التوظيف",
      provider: "لوحة تحكم المزود",
      menu: "القائمة",
      allServices: "كل أركان الحساب",
      loading: "جارِ تحميل بيانات الحساب...",
      loadError: "حدث خطأ أثناء تحميل بيانات الحساب",
      retry: "إعادة المحاولة",
      memberBadge: "عضو موثق",
      coinsBadge: "رصيد النقاط",
      totalBalanceBadge: "إجمالي الرصيد",
      pendingBalanceBadge: "الرصيد المعلق",
      invoicesCount: "الفواتير",
    },
  }[lang];

  const accountNavGroup = [
    { key: "profile", Icon: User, label: labels.profile, desc: isAr ? "تعديل البيانات والرمز" : "Edit personal info & password" },
    { key: "wallet", Icon: Wallet, label: labels.wallet, desc: isAr ? "متابعة الرصيد والنقاط" : "Manage balance & rewards" },
    { key: "addresses", Icon: MapPin, label: labels.addresses, desc: isAr ? "إدارة عناوين الشحن" : "Manage shipping addresses" },
    { key: "devices", Icon: Monitor, label: labels.devices, desc: isAr ? "الأجهزة المسجلة" : "Manage logged-in devices" },
  ];

  const freelancerNavGroup = [
    { key: "my-services", Icon: Wrench, label: labels.myServices, desc: isAr ? "إدارة خدماتك المنشورة" : "Manage your published services" },
    { key: "conversations", Icon: MessageCircle, label: labels.conversations, desc: isAr ? "رسائل ومحادثات العملاء" : "Direct client messaging" },
    { key: "freelancer-orders", Icon: Briefcase, label: labels.freelancerOrders, desc: isAr ? "متابعة أوردارات المستقل" : "Fulfill client orders" },
  ];

  const activityNavGroup = [
    { key: "services", Icon: Store, label: labels.services, desc: isAr ? "طلباتك كعميل" : "Software service orders as client" },
    { key: "courses", Icon: GraduationCap, label: labels.courses, desc: isAr ? "الكورسات والتعليم" : "Courses & learning" },
    { key: "jobs", Icon: Briefcase, label: labels.jobs, desc: isAr ? "طلبات الانضمام" : "Submitted job applications" },
    { key: "invoices", Icon: Receipt, label: labels.invoices, desc: isAr ? "سجل المدفوعات" : "Billing history" },
  ];

  const allNavItems = [...accountNavGroup, ...freelancerNavGroup, ...activityNavGroup];

  const topNavItems = [
    freelancerNavGroup[0],
    freelancerNavGroup[1],
    accountNavGroup[1],
  ];

  const loadAccountData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const profile = await api.apiGet("me");
      const userData = profile?.data || profile?.user;
      if (userData) {
        setUser(userData);
        setForm({
          name: userData.name || "",
          avatar_url: userData.avatar_url || "",
          phone: userData.phone || "",
          address: userData.address || "",
          street_address: userData.street_address || "",
          neighborhood: userData.neighborhood || "",
          city: userData.city || "",
          country_name: userData.country_name || "",
          country_code: userData.country_code || "",
          password: "",
        });
      }

      const invRes = await api.apiGet("invoices").catch(() => null);
      setInvoices(Array.isArray(invRes?.invoices) ? invRes.invoices : []);

      const walletRes = await api.apiGet("rewards/wallet").catch(() => null);
      const wData = walletRes?.data || walletRes?.wallet || walletRes || null;
      setWalletInfo(wData && typeof wData === "object" ? wData : null);

      const bData = await getProviderBalanceBreakdown().catch(() => null);
      setProviderBalance(bData);
    } catch (e) {
      logger.error(e);
      setError(isAr ? "حدث خطأ أثناء تحميل بيانات الحساب" : "Error loading account data");
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => {
    loadAccountData();
  }, [loadAccountData, retryKey]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const allowed = allNavItems.map((n) => n.key);
    if (tab && allowed.includes(tab)) setActiveTab(tab);
  }, [location.search]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";

      const navbar = document.querySelector(".mg-navbar") || document.querySelector("nav");
      const topBar = document.querySelector(".account-top-bar");
      const navH = navbar ? navbar.getBoundingClientRect().height : 52;
      const barH = topBar ? topBar.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty(
        "--navbar-total-height",
        `${Math.round(navH + barH)}px`
      );
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleRetry = useCallback(() => {
    setRetryKey((k) => k + 1);
  }, []);

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Reset hero avatar error whenever the user's avatar URL changes
  useEffect(() => {
    setAvatarHeroError(false);
  }, [user?.avatar_url]);

  const breadcrumbItems = [{ label: labels.myAccount }];
  const shouldShowBreadcrumb = hasBurgerMenu;

  if (loading) {
    return (
      <div className="account-page" dir={isAr ? "rtl" : "ltr"}>
        {shouldShowBreadcrumb && <Breadcrumb items={breadcrumbItems} />}
        <div className="account-hero-loading">
          <LoadingScreen lang={lang} minHeight="65vh" />
        </div>
      </div>
    );
  }

  const coinsBalance = Number(walletInfo?.coins ?? walletInfo?.balance ?? 0);
  const totalBalance = Number(
    providerBalance?.total_balance ??
      (providerBalance?.available_balance || 0) +
        (providerBalance?.held_balance || 0) +
        (providerBalance?.escrow_pending_balance || 0)
  );
  const pendingBalance = Number(
    providerBalance?.escrow_pending_balance ??
      providerBalance?.pending_clearance ??
      providerBalance?.held_balance ??
      0
  );

  const activeTabItem = allNavItems.find((item) => item.key === activeTab) || allNavItems[0];
  const ArrowIcon = isAr ? ChevronLeft : ChevronRight;

  const rawAvatarUrl = user?.avatar_url || "";
  const userAvatarSrc = /^(https?:\/\/|blob:|data:)/i.test(rawAvatarUrl)
    ? rawAvatarUrl
    : toAbsoluteUrl(rawAvatarUrl);

  const showHeroAvatar = Boolean(userAvatarSrc) && !avatarHeroError;

  return (
    <div className="account-page" dir={isAr ? "rtl" : "ltr"}>
      {shouldShowBreadcrumb && <Breadcrumb items={breadcrumbItems} />}

      {/* Modern Luxury User Hero Banner */}
      <section className="account-hero-banner">
        <div className="account-hero-overlay" />
        <div className="account-hero-glow-1" />
        <div className="account-hero-glow-2" />

        <div className="account-hero-content">
          <div className="account-user-card">
            {/* Avatar Container with Glow & Status */}
            <div className="account-avatar-wrapper">
              {showHeroAvatar ? (
                <img
                  key={userAvatarSrc}
                  src={userAvatarSrc}
                  alt={user?.name || "User Avatar"}
                  className="account-avatar-img"
                  onError={(e) => {
                    const currentSrc = e.currentTarget.src || "";
                    if (currentSrc.includes("/laravel-api/storage/") && !e.currentTarget.dataset.retried) {
                      e.currentTarget.dataset.retried = "true";
                      e.currentTarget.src = currentSrc.replace("/laravel-api/storage/", "/laravel-api/public/storage/");
                      return;
                    }
                    setAvatarHeroError(true);
                  }}
                />
              ) : (
                <div className="account-avatar-placeholder">
                  {String(user?.name || "U").trim().charAt(0).toUpperCase()}
                </div>
              )}
              <span className="account-avatar-status" title={labels.memberBadge}>
                <ShieldCheck size={14} />
              </span>
            </div>

            {/* User Meta Info */}
            <div className="account-user-details">
              <div className="account-user-top-row">
                <h1 className="account-user-name">{user?.name || (isAr ? "مستخدم موسى" : "Musa User")}</h1>
                <span className="account-user-badge">
                  <Sparkles size={13} className="account-badge-sparkle" />
                  <span>{labels.memberBadge}</span>
                </span>
              </div>

              <div className="account-user-sub-info">
                {user?.email && (
                  <span className="account-info-chip">
                    <Mail size={14} />
                    <span>{user.email}</span>
                  </span>
                )}
                {user?.phone && (
                  <span className="account-info-chip">
                    <Phone size={14} />
                    <span dir="ltr">{user.phone}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Quick Stats & Action Buttons */}
          <div className="account-hero-actions-container">
            <div className="account-quick-stats">
              <div
                className="account-stat-box"
                onClick={() => handleTabChange("wallet")}
                role="button"
                tabIndex={0}
              >
                <div className="account-stat-icon-wrapper wallet-glow">
                  <Wallet size={18} />
                </div>
                <div className="account-stat-data">
                  <span className="account-stat-number">${totalBalance.toFixed(2)}</span>
                  <span className="account-stat-title">{labels.totalBalanceBadge}</span>
                </div>
              </div>

              <div
                className="account-stat-box"
                onClick={() => handleTabChange("wallet")}
                role="button"
                tabIndex={0}
              >
                <div className="account-stat-icon-wrapper pending-glow">
                  <Clock size={18} />
                </div>
                <div className="account-stat-data">
                  <span className="account-stat-number">${pendingBalance.toFixed(2)}</span>
                  <span className="account-stat-title">{labels.pendingBalanceBadge}</span>
                </div>
              </div>

              <div
                className="account-stat-box"
                onClick={() => handleTabChange("conversations")}
                role="button"
                tabIndex={0}
              >
                <div className="account-stat-icon-wrapper message-glow">
                  <MessageCircle size={18} />
                </div>
                <div className="account-stat-data">
                  <span className="account-stat-number">💬</span>
                  <span className="account-stat-title">{labels.conversations}</span>
                </div>
              </div>
            </div>

            {showProvider && (
              <Link
                to="/provider-dashboard"
                className="account-provider-hero-btn"
                aria-label={labels.provider}
              >
                <span className="account-provider-hero-inner">
                  <Store size={18} strokeWidth={2} />
                  <span>{labels.provider}</span>
                  <ArrowUpRight size={16} strokeWidth={2.5} />
                </span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Mobile Top Navigation Pills Bar */}
      {hasBurgerMenu && (
        <div className="account-top-bar">
          <div className="account-top-bar-inner">
            {topNavItems.map((item) => {
              const Icon = item.Icon;
              const isActive = activeTab === item.key;
              return (
                <button
                  key={item.key}
                  className={`account-top-item ${isActive ? "active" : ""}`}
                  onClick={() => handleTabChange(item.key)}
                  type="button"
                >
                  <Icon size={16} strokeWidth={2.5} />
                  <span>{item.label}</span>
                </button>
              );
            })}
            <button
              className={`account-top-item ${menuOpen ? "active" : ""}`}
              onClick={() => setMenuOpen(true)}
              type="button"
            >
              <LayoutGrid size={16} strokeWidth={2.5} />
              <span>{labels.menu}</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Account Grid Layout */}
      <div className="account-layout">
        {/* Modern Sidebar Navigation */}
        <aside className="account-sidebar" role="navigation" aria-label={labels.myAccount}>
          <div className="account-nav-card">
            {/* Group 1: Freelancer Workspace */}
            <div className="account-nav-section">
              <div className="account-nav-category-title">
                {labels.freelancerWorkspace}
              </div>
              <div className="account-nav-list">
                {freelancerNavGroup.map((item) => {
                  const Icon = item.Icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button
                      key={item.key}
                      className={`account-nav-item ${isActive ? "active" : ""}`}
                      onClick={() => handleTabChange(item.key)}
                      type="button"
                      aria-current={isActive ? "page" : undefined}
                    >
                      <span className="account-nav-icon">
                        <Icon size={18} strokeWidth={2} />
                      </span>
                      <div className="account-nav-text">
                        <span className="account-nav-label">{item.label}</span>
                      </div>
                      <ArrowIcon size={15} className="account-nav-arrow" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="account-nav-divider" />

            {/* Group 2: Account Settings */}
            <div className="account-nav-section">
              <div className="account-nav-category-title">
                {labels.accountSettings}
              </div>
              <div className="account-nav-list">
                {accountNavGroup.map((item) => {
                  const Icon = item.Icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button
                      key={item.key}
                      className={`account-nav-item ${isActive ? "active" : ""}`}
                      onClick={() => handleTabChange(item.key)}
                      type="button"
                      aria-current={isActive ? "page" : undefined}
                    >
                      <span className="account-nav-icon">
                        <Icon size={18} strokeWidth={2} />
                      </span>
                      <div className="account-nav-text">
                        <span className="account-nav-label">{item.label}</span>
                      </div>
                      <ArrowIcon size={15} className="account-nav-arrow" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="account-nav-divider" />

            {/* Group 3: Client Activities */}
            <div className="account-nav-section">
              <div className="account-nav-category-title">
                {labels.activitiesAndServices}
              </div>
              <div className="account-nav-list">
                {activityNavGroup.map((item) => {
                  const Icon = item.Icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button
                      key={item.key}
                      className={`account-nav-item ${isActive ? "active" : ""}`}
                      onClick={() => handleTabChange(item.key)}
                      type="button"
                      aria-current={isActive ? "page" : undefined}
                    >
                      <span className="account-nav-icon">
                        <Icon size={18} strokeWidth={2} />
                      </span>
                      <div className="account-nav-text">
                        <span className="account-nav-label">{item.label}</span>
                      </div>
                      <ArrowIcon size={15} className="account-nav-arrow" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content Workspace */}
        <main className="account-main">
          {/* Header of Active Tab View */}
          <div className="account-tab-header">
            <div className="account-tab-title-group">
              <div className="account-tab-icon">
                <activeTabItem.Icon size={22} strokeWidth={2} />
              </div>
              <div>
                <h2 className="account-tab-title">{activeTabItem.label}</h2>
                <p className="account-tab-subtitle">{activeTabItem.desc}</p>
              </div>
            </div>
          </div>

          {error ? (
            <div className="account-error">
              <div className="account-error-icon">
                <AlertTriangle size={40} strokeWidth={1.5} />
              </div>
              <p className="account-error-message">{error}</p>
              <button className="account-btn-primary" onClick={handleRetry} type="button">
                {labels.retry}
              </button>
            </div>
          ) : (
            <div className="account-content-card">
              {activeTab === "profile" && (
                <ProfileTab
                  user={user}
                  setUser={setUser}
                  form={form}
                  setForm={setForm}
                  isAr={isAr}
                  labels={labels}
                  onOpenAddresses={() => handleTabChange("addresses")}
                  onProfileUpdated={(updatedUser) => {
                    if (updatedUser) {
                      setUser(updatedUser);
                    }
                  }}
                />
              )}

              {activeTab === "my-services" && <MyServicesTab />}
              {activeTab === "conversations" && <ConversationsTab />}
              {activeTab === "freelancer-orders" && <MyFreelancerOrders />}
              {activeTab === "addresses" && <AddressesTab isAr={isAr} />}
              {activeTab === "devices" && <DevicesTab isAr={isAr} />}
              {activeTab === "invoices" && <MyInvoices isAr={isAr} lang={lang} />}
              {activeTab === "courses" && <MyCourses embedded />}
              {activeTab === "jobs" && <MyJobApplications embedded />}
              {activeTab === "services" && <MyServiceOrders embedded />}
              {activeTab === "wallet" && <ProfileWalletTab isAr={isAr} />}
            </div>
          )}
        </main>
      </div>

      {/* Responsive Grid Navigation Drawer Modal */}
      {hasBurgerMenu && menuOpen && (
        <>
          <div
            className="account-menu-backdrop"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="account-menu-modal" role="dialog" aria-modal="true">
            <div className="account-menu-header">
              <div className="d-flex align-items-center gap-2">
                <Sparkles size={20} className="text-warning" />
                <h2>{labels.allServices}</h2>
              </div>
              <button
                className="account-menu-close"
                onClick={() => setMenuOpen(false)}
                type="button"
                aria-label="Close"
              >
                <X size={20} strokeWidth={2} />
              </button>
            </div>

            <div className="account-menu-body">
              <div className="account-menu-group-title">{labels.freelancerWorkspace}</div>
              <div className="account-menu-grid">
                {freelancerNavGroup.map((item) => {
                  const Icon = item.Icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button
                      key={item.key}
                      className={`account-menu-card ${isActive ? "active" : ""}`}
                      onClick={() => handleTabChange(item.key)}
                      type="button"
                    >
                      <span className="account-menu-card-icon">
                        <Icon size={26} strokeWidth={2} />
                      </span>
                      <span className="account-menu-card-label">{item.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="account-menu-group-title mt-4">{labels.accountSettings}</div>
              <div className="account-menu-grid">
                {accountNavGroup.map((item) => {
                  const Icon = item.Icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button
                      key={item.key}
                      className={`account-menu-card ${isActive ? "active" : ""}`}
                      onClick={() => handleTabChange(item.key)}
                      type="button"
                    >
                      <span className="account-menu-card-icon">
                        <Icon size={26} strokeWidth={2} />
                      </span>
                      <span className="account-menu-card-label">{item.label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="account-menu-group-title mt-4">{labels.activitiesAndServices}</div>
              <div className="account-menu-grid">
                {activityNavGroup.map((item) => {
                  const Icon = item.Icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button
                      key={item.key}
                      className={`account-menu-card ${isActive ? "active" : ""}`}
                      onClick={() => handleTabChange(item.key)}
                      type="button"
                    >
                      <span className="account-menu-card-icon">
                        <Icon size={26} strokeWidth={2} />
                      </span>
                      <span className="account-menu-card-label">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}